import { useEffect, useRef, type ComponentProps, type RefObject } from 'react';
import type { PopoverContent } from '@crono-one/design-system';

type OverlayBoundary = Pick<
  ComponentProps<typeof PopoverContent>,
  'collisionPadding' | 'collisionBoundary'
>;

const paddingToRootMargin = (
  padding: OverlayBoundary['collisionPadding'] = 0,
): string => {
  const {
    top = 0,
    right = 0,
    bottom = 0,
    left = 0,
  } = typeof padding === 'number'
    ? { top: padding, right: padding, bottom: padding, left: padding }
    : padding;
  return [top, right, bottom, left].map((px) => `${-px}px`).join(' ');
};

/**
 * Calls `onClose` once the anchor is fully outside the overlay's boundary,
 * shrunk by its collision padding: the same area where `hideWhenDetached`
 * would hide the overlay. Lets an overlay whose anchor scrolls away (e.g. in
 * a table cell) close instead of coming back when the anchor scrolls back.
 */
export function useCloseWhenDetached({
  anchorRef,
  enabled,
  overlayProps,
  onClose,
}: {
  anchorRef: RefObject<HTMLElement>;
  enabled: boolean;
  overlayProps?: OverlayBoundary;
  onClose: () => void;
}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const boundary = overlayProps?.collisionBoundary;
  const root = boundary instanceof Element ? boundary : null;
  const rootMargin = paddingToRootMargin(overlayProps?.collisionPadding);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!enabled || !anchor || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) onCloseRef.current();
      },
      { root, rootMargin },
    );
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [anchorRef, enabled, root, rootMargin]);
}
